import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { MessageKey } from '@bookingdei/i18n/message-key';
import { formatPhone } from '@bookingdei/i18n/phone';
import { phonePlan } from '../../theme/phone-plan';
import { BottomSheet } from '../../shell/bottom-sheet';
import { useLanguage } from '../../shell/language-context';
import { OfferArt } from '../../shell/offer-art';
import { useToast } from '../../shell/toast';
import { PhotoLightbox } from '../business-page/business-gallery';
import type { OfferView } from './offer-view';
import { api } from '../../api';
import { useResource } from '../../shell/use-resource';
import { reelEmbed } from '../reels/reel-embed';
import { IconArrowLeft, IconPlay } from '../reels/reel-icons';
import { ReelViewer, type ReelCard } from '../reels/reel-viewer';
import '../reels/reels.css';

const CTA_LABEL: Record<NonNullable<OfferView['cta']>['kind'], MessageKey> = {
  listing: 'dc52.cta.listing',
  call: 'dc52.cta.call',
  whatsapp: 'dc52.cta.whatsapp',
  website: 'dc52.cta.website',
  facebook: 'dc52.cta.facebook',
  link: 'dc52.cta.link',
};

// The offer popup (design "Offer Card Options", option 4): a centred two-column modal
// on desktop, a bottom sheet on a phone (shell BottomSheet). Left, the creative on a
// blurred stage with a tap-to-fullscreen; right, the facts a reader decides on — deal
// and expiry chips, the business with a way to its page, the before/after price the
// server computed, the terms, the validity window (FR-C19), the phone when the business
// asked to be called, and the one action the offer supports: "Book now" into the
// stepper, or the business's own call / WhatsApp / page for a deal (FR-A26 v3). The
// disclaimer states the platform rule: payment is between the customer and the business.
export function OfferPopup({ view: v, onClose }: { view: OfferView; onClose: () => void }) {
  const { t, lang } = useLanguage();
  const toast = useToast();
  const [zoom, setZoom] = useState(false);
  // B3 (FR-C29): the reels that promote this offer ride a thumb rail on the creative. On a
  // desktop a thumb swaps the stage to the 9:16 player; on a phone it opens the viewer.
  const reels = useResource<ReelCard[]>(
    async (signal) => {
      if (!v.bizSlug || !v.offerId) return [];
      const res = await api.GET('/reels', { params: { query: { business: v.bizSlug } }, signal, lang });
      return res.items.filter((r) => r.offer_id === v.offerId);
    },
    [v.bizSlug, v.offerId, lang],
  );
  const reelList = reels.data ?? [];
  const [media, setMedia] = useState<'creative' | number>('creative');
  const [viewer, setViewer] = useState<number | null>(null);
  const pickReel = (i: number) => {
    if (window.matchMedia('(max-width: 700px)').matches) setViewer(i);
    else setMedia(i);
  };
  const reel = media === 'creative' ? null : reelList[media];
  const reelSrc = reel ? reelEmbed(reel.video_id) : null;

  const copy = async (text: string, done: MessageKey) => {
    try {
      await navigator.clipboard?.writeText(text);
      toast.show(t(done));
    } catch {
      toast.show(t('dc52.popup.copy_failed'));
    }
  };
  const share = async () => {
    const url = v.bizHref ? new URL(v.bizHref, window.location.origin).toString() : window.location.href;
    const title = `${v.bizName} — ${v.title}`;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
        return;
      } catch {
        /* the user dismissed the sheet — fall through to copying */
      }
    }
    await copy(url, 'dc52.popup.link_copied');
  };

  let action: ReactNode = null;
  if (v.bookHref) {
    action = (
      <Link className="ofpp__btn" to={v.bookHref}>
        {t('dc52.book_now')}
      </Link>
    );
  } else if (v.cta && v.cta.kind !== 'listing') {
    const outside = v.cta.href.startsWith('https://');
    action = (
      <a className="ofpp__btn" href={v.cta.href} {...(outside ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
        {t(CTA_LABEL[v.cta.kind])}
      </a>
    );
  }

  return (
    <>
      <BottomSheet open onClose={onClose} title={`${v.bizName} — ${v.title}`} grabber hideHead className="sheet--offer">
        <div className="ofpp">
          <div className={reelSrc ? 'ofpp__stage ofpp__stage--reel' : 'ofpp__stage'}>
            {reelSrc && reel ? (
              <>
                <iframe
                  className="rv__player"
                  src={reelSrc.embed()}
                  title={reel.caption ?? v.bizName}
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  referrerPolicy="strict-origin-when-cross-origin"
                />
                <button type="button" className="ofpp__back" onClick={() => setMedia('creative')}>
                  <IconArrowLeft /> {t('reels.creative')}
                </button>
                <div className="ofpp__reelcap">
                  {reel.caption}
                  <span>{reel.creator ?? v.bizName}</span>
                </div>
              </>
            ) : v.image ? (
              <>
                <span className="ofpp__blur" style={{ backgroundImage: `url('${v.image.poster}')` }} aria-hidden="true" />
                <button type="button" className="ofpp__zoom" onClick={() => setZoom(true)} aria-label={t('dc52.popup.full_image')}>
                  <img className="ofpp__img" src={v.image.full} alt="" loading="lazy" decoding="async" />
                </button>
                <button type="button" className="ofpp__zoomchip" onClick={() => setZoom(true)}>
                  ⤢ {t('dc52.popup.full_image')}
                </button>
              </>
            ) : (
              <OfferArt />
            )}
            {reelList.length ? (
              <div className="ofpp__thumbs" role="group" aria-label={t('biz.reels')}>
                <button
                  type="button"
                  className={media === 'creative' ? 'ofpp__th is-on' : 'ofpp__th'}
                  style={v.image ? { backgroundImage: `url('${v.image.poster}')` } : undefined}
                  aria-label={t('reels.creative')}
                  aria-pressed={media === 'creative'}
                  onClick={() => setMedia('creative')}
                />
                {reelList.map((r, i) => {
                  const e = reelEmbed(r.video_id);
                  return (
                    <button key={r.id} type="button" className={media === i ? 'ofpp__th is-on' : 'ofpp__th'} aria-label={t('reels.reel_n', { n: String(i + 1) })} aria-pressed={media === i} onClick={() => pickReel(i)}>
                      {e ? <img src={e.poster} alt="" loading="lazy" /> : null}
                      <span aria-hidden="true">
                        <IconPlay size={12} />
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : null}
          </div>
          <div className="ofpp__info">
            <div className="ofpp__chips">
              <span className="ofp__chip ofp__chip--deal">{v.off}</span>
              <span className={v.ends.urgent ? 'ofp__chip ofp__chip--ends is-urgent' : 'ofp__chip ofp__chip--ends'}>⏳ {v.ends.label}</span>
              {v.sponsored ? <span className="ofp__chip">{t('dc52.sponsored')}</span> : null}
              {v.appliesTo ? <span className="ofp__chip">{t(v.appliesTo === 'account' ? 'dc52.applies.account' : 'dc52.applies.business')}</span> : null}
            </div>
            <h3 className="ofpp__title">{v.title}</h3>
            <div className="ofpp__biz">
              <span className="ofpp__tile" aria-hidden="true">
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 10v10h16V10" />
                  <path d="M2 9l2-5h16l2 5a3 3 0 0 1-6 0 3 3 0 0 1-6 0 3 3 0 0 1-6 0z" />
                  <path d="M9.5 20v-5.5h5V20" />
                </svg>
              </span>
              <span className="ofpp__bizcol">
                <b>
                  {v.bizName}
                  {v.verified ? <span className="ofpp__ver"> ✓ {t('dc52.verified')}</span> : null}
                </b>
                <span>{[v.category, v.area].filter(Boolean).join(' · ')}</span>
              </span>
              {v.bizHref ? (
                <Link className="ofpp__link" to={v.bizHref}>
                  {t('dc52.popup.view_biz')} →
                </Link>
              ) : null}
            </div>
            {v.priceAfter ? (
              <div className="ofpp__price">
                <span className="ofpp__tile ofpp__tile--amber" aria-hidden="true">
                  🏷
                </span>
                <span className="ofpp__pricecol">
                  <span className="ofpp__prow">
                    {v.priceBefore ? <s>{v.priceBefore}</s> : null}
                    <b>{v.priceAfter}</b>
                  </span>
                </span>
              </div>
            ) : null}
            {v.terms ? <p className="ofpp__desc">{v.terms}</p> : null}
            {!v.bookHref ? <div className="ofpp__warn">ⓘ {t('dc52.popup.not_bookable')}</div> : null}
            <dl className="ofpp__facts">
              <div className="ofpp__fact">
                <dt>{t('dc52.popup.validity')}</dt>
                <dd>{v.validity}</dd>
              </div>
              {v.area || v.category ? (
                <div className="ofpp__fact">
                  <dt>{t('dc52.popup.location')}</dt>
                  <dd>{[v.area, v.category].filter(Boolean).join(', ')}</dd>
                </div>
              ) : null}
              {v.phone ? (
                <div className="ofpp__fact">
                  <dt>{t('dc52.popup.phone')}</dt>
                  <dd className="ofpp__mono">{formatPhone(v.phone, phonePlan)}</dd>
                  <button type="button" className="ofpp__mini" onClick={() => copy(v.phone!, 'dc52.popup.number_copied')}>
                    {t('dc52.popup.copy')}
                  </button>
                </div>
              ) : null}
            </dl>
            <div className="ofpp__actions">
              {action}
              <button type="button" className="ofpp__btn ofpp__btn--ghost" onClick={share}>
                {t('dc52.popup.share')}
              </button>
            </div>
            <p className="ofpp__disc">{t('dc52.popup.disclaimer')}</p>
          </div>
        </div>
      </BottomSheet>
      {viewer !== null ? <ReelViewer reels={reelList} start={viewer} onClose={() => setViewer(null)} /> : null}
      {zoom && v.image ? <PhotoLightbox photos={[{ thumb: v.image.poster, tile: v.image.poster, full: v.image.full }]} start={0} name={v.bizName} onClose={() => setZoom(false)} /> : null}
    </>
  );
}

// One popup per surface: the surface keeps the open offer and renders `popup`.
export function useOfferPopup(): { open: (view: OfferView) => void; popup: ReactNode } {
  const [view, setView] = useState<OfferView | null>(null);
  return {
    open: setView,
    popup: view ? <OfferPopup view={view} onClose={() => setView(null)} /> : null,
  };
}
